import {
  NAVBAR_HEIGHT,
  SOCIAL_LINKS,
} from '#app/components/AppBar/constants.js';
import { Button } from '#app/components/Buttons/Button.js';
import { Link } from '#app/components/Link/index.js';
import { useModal } from '#app/components/Modals/V2/GlobalModalSystem/useModal.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useIsSmallScreen } from '#app/hooks/useIsSmallScreen.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { HamburgerMenuIcon } from '@contra/icons/icons';
import { getRoute } from '@contra/routes';
import { usePreventScroll } from '@react-aria/overlays';
import { useCallback, useState } from 'react';
import { css, styled } from 'styled-components';
import { resolveRoute } from 'vike/routing';

const Layout = styled.div`
  ${({ theme }) => css`
    display: flex;
    margin-left: auto;
    align-items: center;
    z-index: 1;
    position: relative;

    & > button {
      z-index: 2;
    }

    & > * + * {
      margin-left: 16px;

      ${theme.mediaQueries.sm} {
        margin-left: 16px;
      }
    }
  `}
`;

const HamburgerButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  z-index: 2;
`;

const MobileMenuContainer = styled.div`
  ${({ theme }) => css`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: center;
    background-color: ${theme.colorsV2.white};
    padding: ${NAVBAR_HEIGHT + 20}px 0 0;
    margin: 0;
    z-index: -1;
  `}
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  align-items: center;
`;

const SocialLinksContainer = styled.div`
  margin-top: 16px;
  display: flex;
  gap: 16px;
`;

export const LoggedOutAppBarItems = () => {
  const location = useLocation();
  const { track } = useAnalytics();

  // If we are on the opportunity/job page, we want to override the user type to
  // independent so that we can reduce how many independets accidentally sign up as clients.
  // See https://linear.app/contra/issue/DGR-2144/improve-unauth-opportunity-apply-flow
  const { match: isJobPage } = resolveRoute(
    '/opportunity/@slug',
    location.pathname,
  );
  const overrideUserType = isJobPage ? 'independent' : undefined;
  const { signUpPath } = useOnboardingRedirection({
    overrideUserType,
    redirectTo: location.path,
  });

  const { openModal: openAnonymousJobCreationModal } = useModal({
    identifier: 'AnonymousJobCreationModal',
    modalProps: {},
  });

  const handlePostAJobClick = useCallback(() => {
    track('button', {
      action: 'tapped',
      button_name: 'post_job',
      copy: 'Post a job',
      section: 'unauthenticated_app_bar',
    });

    openAnonymousJobCreationModal();
  }, [openAnonymousJobCreationModal, track]);

  const [mobileMenuIsOpen, setMobileMenuIsOpen] = useState(false);
  const isSmallScreen = useIsSmallScreen();
  const onHamburgerClick = useCallback(() => {
    setMobileMenuIsOpen((previous) => !previous);
  }, [setMobileMenuIsOpen]);
  usePreventScroll({ isDisabled: !mobileMenuIsOpen });

  return (
    <>
      {isSmallScreen ? (
        <Layout>
          <Button
            as={Link}
            to={signUpPath}
            variant="V2Primary"
          >
            Sign Up
          </Button>
          <HamburgerButton
            aria-label="Open menu"
            onClick={onHamburgerClick}
          >
            <HamburgerMenuIcon />
          </HamburgerButton>
          {mobileMenuIsOpen && (
            <MobileMenuContainer>
              <ButtonContainer>
                <Button
                  onClick={handlePostAJobClick}
                  variant="V2Link"
                >
                  Post a job
                </Button>

                <Button
                  as={Link}
                  to={getRoute('/log-in', {
                    search: {
                      redirectTo: location.path,
                    },
                  })}
                  variant="V2Link"
                >
                  Log In
                </Button>
              </ButtonContainer>

              <SocialLinksContainer>
                {SOCIAL_LINKS.map((socialLink) => (
                  <a
                    href={socialLink.link}
                    key={socialLink.link}
                    rel="noreferrer noopener"
                    target="_blank"
                  >
                    {socialLink.icon}
                  </a>
                ))}
              </SocialLinksContainer>
            </MobileMenuContainer>
          )}
        </Layout>
      ) : (
        <Layout>
          <Button
            onClick={handlePostAJobClick}
            variant="V2Secondary"
          >
            Post a job
          </Button>

          <Button
            as={Link}
            to={signUpPath}
            variant="V2Primary"
          >
            Sign Up
          </Button>

          <Button
            as={Link}
            to={getRoute('/log-in', {
              search: {
                redirectTo: location.path,
              },
            })}
            variant="V2Link"
          >
            Log In
          </Button>
        </Layout>
      )}
    </>
  );
};
